// SAB-572: native-only navigator.geolocation shim.
//
// The iOS/Android app is a Capacitor WKWebView/WebView that REMOTE-LOADS prod
// (capacitor.config.ts server.url). In that container the browser
// `navigator.geolocation` is NOT wired to CoreLocation, so Locance's HTML5 SDK
// (which calls `navigator.geolocation.getCurrentPosition` from the top document
// via window.lshtml5 — see useLocanceGeoCheck.ts / locance.ts) fails with no
// permission prompt. That's the SAB-572 symptom: GeoBlockScreen "Couldn't get
// your location", app never prompts.
//
// This routes navigator.geolocation through @capacitor/geolocation
// (CLLocationManager + the native prompt driven by NSLocationWhenInUseUsageDescription),
// mapping the Capacitor result back to the standard GeolocationPosition /
// GeolocationPositionError shapes Locance expects — so the SDK's UNMODIFIED calls
// work, and the existing acquireGeoSession() fail-closed path (denied -> null ->
// GeoBlockScreen) behaves exactly as it does on web.
//
// NATIVE-ONLY: gated on Capacitor.isNativePlatform(). On web/desktop the real
// browser geolocation already works with the normal site prompt, so we DO NOT touch
// it there — web behavior is completely unchanged.
//
// Installed synchronously at app entry (main.tsx) BEFORE the Locance SDK ever loads
// (Locance scripts load lazily on the first money-action gather, long after boot),
// so the SDK only ever sees the shimmed navigator.geolocation.

import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

let installed = false;

// SAB-644 TEMP diagnostic: record the last native geolocation outcome so the geo-gather beacon
// can report whether a fix actually reached the collector (resolves the shim-shape/return-type
// and does-the-collector-even-call-us questions). Only ever populated on native (shim installed
// only there). Remove with the rest of the SAB-644 beacon instrumentation.
export interface GeoShimDiag {
  method: 'getCurrentPosition' | 'watchPosition';
  delivered: boolean;
  error: string | null;
  ts: number;
  // RAW Capacitor Position (pre-conversion) + the post-toBrowserPosition object. Held as the
  // actual objects; the beacon derives a privacy-safe shape (no lat/long values) from them. The
  // raw value is the evidence for whether Capacitor itself returns null/0 accuracy vs our ?? 0.
  rawPos: unknown;
  convertedPos: unknown;
  callbackCount: number; // watch fires repeatedly — count success deliveries since load
}
let lastGeoDiag: GeoShimDiag | null = null;
let deliveredCount = 0;
export function getGeoShimDiag(): GeoShimDiag | null { return lastGeoDiag; }

// SAB-644: ordered log of EVERY geolocation call across the whole gather — both
// getCurrentPosition and watchPosition, invocation + each success/error with code+message. A
// code-2 error on a separate getCurrentPosition call would be invisible in a watch-only view.
// Reset per gather by run(); the beacon reads it. Capped so it can't grow unbounded.
export interface GeoCall {
  seq: number;
  method: 'getCurrentPosition' | 'watchPosition';
  kind: 'invoke' | 'success' | 'error';
  code?: number;
  message?: string;
  ts: number;
}
let geoCallLog: GeoCall[] = [];
let geoSeq = 0;
const GEO_LOG_CAP = 40;
export function resetGeoShimLog(): void { geoCallLog = []; geoSeq = 0; }
export function getGeoShimLog(): GeoCall[] { return geoCallLog.slice(0, GEO_LOG_CAP); }
function logGeoCall(method: GeoCall['method'], kind: GeoCall['kind'], code?: number, message?: string) {
  if (geoCallLog.length >= GEO_LOG_CAP) return;
  geoCallLog.push({
    seq: geoSeq++, method, kind, ts: Date.now(),
    ...(code !== undefined ? { code } : {}),
    ...(message ? { message: String(message).slice(0, 120) } : {}),
  });
}
function recordGeoDiag(
  method: GeoShimDiag['method'],
  delivered: boolean,
  error: string | null,
  rawPos: unknown = null,
  convertedPos: unknown = null,
) {
  if (delivered) deliveredCount++;
  lastGeoDiag = { method, delivered, error, ts: Date.now(), rawPos, convertedPos, callbackCount: deliveredCount };
}

function toBrowserPosition(p: {
  coords: {
    latitude: number; longitude: number; accuracy: number;
    altitude?: number | null; altitudeAccuracy?: number | null;
    heading?: number | null; speed?: number | null;
  };
  timestamp: number;
}): GeolocationPosition {
  const coords = {
    latitude: p.coords.latitude,
    longitude: p.coords.longitude,
    accuracy: p.coords.accuracy ?? 0,
    altitude: p.coords.altitude ?? null,
    altitudeAccuracy: p.coords.altitudeAccuracy ?? null,
    // SAB-644: CoreLocation uses -1 as the "unknown" sentinel for course (heading) and speed;
    // a real browser reports NULL there. Capacitor passes -1 straight through, and Locance's
    // HTML5 collector reads speed + heading — a `speed >= 0` / `heading >= 0` validator rejects
    // the whole fix as POSITION_UNAVAILABLE (code 2), deterministically, on every callback.
    // Normalise negatives to null to match browser behaviour.
    heading: typeof p.coords.heading === 'number' && p.coords.heading >= 0 ? p.coords.heading : null,
    speed: typeof p.coords.speed === 'number' && p.coords.speed >= 0 ? p.coords.speed : null,
    toJSON() { return this; },
  } as GeolocationCoordinates;
  return {
    coords,
    timestamp: p.timestamp ?? Date.now(),
    toJSON() { return this; },
  } as GeolocationPosition;
}

function toBrowserError(code: 1 | 2 | 3, message: string): GeolocationPositionError {
  return {
    code,
    message,
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3,
  } as GeolocationPositionError;
}

// Map a Capacitor error message to the closest standard GeolocationPositionError code
// so Locance's classifier (consent_denied vs transient vs unavailable) still works.
function classifyError(message: string): 1 | 2 | 3 {
  if (/denied|permission/i.test(message)) return 1; // PERMISSION_DENIED
  if (/timeout|timed out/i.test(message)) return 3;  // TIMEOUT
  return 2;                                           // POSITION_UNAVAILABLE
}

async function ensurePermission(): Promise<boolean> {
  try {
    const perm = await Geolocation.checkPermissions();
    if (perm.location === 'granted') return true;
    if (perm.location === 'denied') return false;
    // 'prompt' / 'prompt-with-rationale' -> trigger the native OS prompt.
    const req = await Geolocation.requestPermissions({ permissions: ['location'] });
    return req.location === 'granted';
  } catch {
    // If the permission APIs throw, let getCurrentPosition try and surface its own error.
    return true;
  }
}

const getCurrentPosition = (
  success: PositionCallback,
  error?: PositionErrorCallback | null,
  options?: PositionOptions,
): void => {
  logGeoCall('getCurrentPosition', 'invoke');
  void (async () => {
    try {
      const ok = await ensurePermission();
      if (!ok) {
        logGeoCall('getCurrentPosition', 'error', 1, 'permission denied');
        recordGeoDiag('getCurrentPosition', false, 'permission denied');
        error?.(toBrowserError(1, 'User denied Geolocation'));
        return;
      }
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 30_000,
        maximumAge: options?.maximumAge ?? 0,
      });
      const converted = toBrowserPosition(pos);
      logGeoCall('getCurrentPosition', 'success');
      recordGeoDiag('getCurrentPosition', true, null, pos, converted);
      success(converted);
    } catch (e) {
      const msg = (e as Error)?.message ?? 'Location unavailable';
      logGeoCall('getCurrentPosition', 'error', classifyError(msg), msg);
      recordGeoDiag('getCurrentPosition', false, msg);
      error?.(toBrowserError(classifyError(msg), msg));
    }
  })();
};

// Locance's HTML5 collector primarily uses getCurrentPosition, but shim watchPosition
// too in case the SDK watches. Maps browser watch-id (number) <-> Capacitor id (string).
const watchIds = new Map<number, string>();
let nextWatchId = 1;

const watchPosition = (
  success: PositionCallback,
  error?: PositionErrorCallback | null,
  options?: PositionOptions,
): number => {
  const id = nextWatchId++;
  logGeoCall('watchPosition', 'invoke');
  Geolocation.watchPosition(
    {
      enableHighAccuracy: options?.enableHighAccuracy ?? true,
      timeout: options?.timeout ?? 30_000,
      maximumAge: options?.maximumAge ?? 0,
    },
    (pos, err) => {
      if (err) {
        logGeoCall('watchPosition', 'error', classifyError(err.message ?? ''), err.message ?? 'watch error');
        recordGeoDiag('watchPosition', false, err.message ?? 'watch error');
        error?.(toBrowserError(classifyError(err.message ?? ''), err.message ?? 'watch error'));
        return;
      }
      if (pos) {
        const converted = toBrowserPosition(pos);
        logGeoCall('watchPosition', 'success');
        recordGeoDiag('watchPosition', true, null, pos, converted);
        success(converted);
      }
    },
  )
    .then((watchId) => { watchIds.set(id, watchId); })
    .catch((e) => {
      logGeoCall('watchPosition', 'error', 2, (e as Error)?.message ?? 'watch failed');
      recordGeoDiag('watchPosition', false, (e as Error)?.message ?? 'watch failed');
      error?.(toBrowserError(2, (e as Error)?.message ?? 'watch failed'));
    });
  return id;
};

const clearWatch = (id: number): void => {
  const wid = watchIds.get(id);
  if (wid) {
    void Geolocation.clearWatch({ id: wid }).catch(() => {});
    watchIds.delete(id);
  }
};

/**
 * Install the native geolocation shim. No-op on web/desktop and if already installed.
 * Call once, synchronously, at app entry (before any Locance gather).
 */
export function installNativeGeolocationShim(): void {
  if (installed) return;
  if (!Capacitor.isNativePlatform()) return; // web/desktop: leave browser geolocation untouched
  installed = true;

  const shim = { getCurrentPosition, watchPosition, clearWatch };
  try {
    Object.defineProperty(navigator, 'geolocation', { value: shim, configurable: true });
  } catch {
    // Some engines expose navigator.geolocation as non-redefinable; patch methods in place.
    try {
      const g = navigator.geolocation as unknown as Record<string, unknown>;
      g.getCurrentPosition = getCurrentPosition;
      g.watchPosition = watchPosition;
      g.clearWatch = clearWatch;
    } catch {
      // Give up silently — the existing fail-closed path will surface a GeoBlockScreen.
    }
  }
}
